import { ApiError, forgetSession, onUnauthenticated, queryKeys, rememberSession } from '@ottd/api';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import { getMe, postLogin, postLogout, postRegister, postTwoFactorChallenge } from './api';
import { AuthContext } from './auth-context';
import type { AuthContextValue } from './auth-context';
import type {
  AuthenticatedUser,
  AuthStatus,
  LoginPayload,
  LoginResult,
  RegisterPayload,
} from './types';

/*
 * Bootstraps the Sanctum session from GET /api/auth/me and exposes the login /
 * register / logout / 2FA transitions. Adapted from integrator's AuthProvider;
 * same-origin cookie, so no token storage. Drives the nav's logged-in state.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<AuthStatus>('idle');
  const [user, setUser] = useState<AuthenticatedUser | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const mountedRef = useRef(true);

  // Drop account-scoped cache (in-game `!login` token, IP lookups) whenever the
  // session ends, so on a shared device the next user can't read the previous
  // one's data from a stale query. Public read caches are left intact.
  const clearUserScopedCache = useCallback(() => {
    queryClient.removeQueries({ queryKey: queryKeys.account.all });
    queryClient.removeQueries({ queryKey: queryKeys.ip.all });
    queryClient.removeQueries({ queryKey: queryKeys.auth.me });
  }, [queryClient]);

  useEffect(() => {
    // Reset on every mount so StrictMode's simulated unmount doesn't strand
    // the provider on a stale `false` (integrator's documented gotcha).
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refresh = useCallback(async (): Promise<AuthenticatedUser | null> => {
    setStatus('loading');
    try {
      const me = await getMe();
      // Remember that this browser has held a session, so a later 401 can be
      // reported as an expiry rather than mistaken for a first-time guest.
      rememberSession();
      if (!mountedRef.current) return me;
      setUser(me);
      setStatus('authenticated');
      setError(null);
      return me;
    } catch (caught) {
      if (!mountedRef.current) return null;
      setUser(null);
      setStatus('guest');
      // A 401 is the normal "not logged in" path - not a surfaced error.
      setError(
        caught instanceof ApiError && caught.isUnauthenticated
          ? null
          : caught instanceof Error
            ? caught
            : new Error('Failed to load session'),
      );
      return null;
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    // Any 401/419 anywhere flips the session to guest so route guards bounce
    // to /login rather than stranding the user on a dead page.
    return onUnauthenticated(() => {
      if (!mountedRef.current) return;
      setUser(null);
      setStatus('guest');
      setError(null);
      clearUserScopedCache();
    });
  }, [clearUserScopedCache]);

  const login = useCallback(
    async (payload: LoginPayload): Promise<LoginResult> => {
      setError(null);
      const { twoFactorRequired } = await postLogin(payload);
      if (twoFactorRequired) return { kind: 'two_factor_required' };
      const fresh = await refresh();
      if (fresh === null) throw new Error('Login succeeded but the session could not be loaded');
      return { kind: 'authenticated', user: fresh };
    },
    [refresh],
  );

  const verifyTwoFactor = useCallback(
    async (input: { code?: string; recoveryCode?: string }): Promise<AuthenticatedUser> => {
      setError(null);
      await postTwoFactorChallenge({ code: input.code, recovery_code: input.recoveryCode });
      const fresh = await refresh();
      if (fresh === null)
        throw new Error('Verification succeeded but the session could not be loaded');
      return fresh;
    },
    [refresh],
  );

  const register = useCallback(
    async (payload: RegisterPayload): Promise<AuthenticatedUser> => {
      setError(null);
      await postRegister(payload);
      const fresh = await refresh();
      if (fresh === null)
        throw new Error('Registration succeeded but the session could not be loaded');
      return fresh;
    },
    [refresh],
  );

  const logout = useCallback(async (): Promise<void> => {
    try {
      await postLogout();
    } finally {
      // Forget account-scoped cache regardless of mount state so a shared
      // device never serves the logged-out user's data to the next one.
      clearUserScopedCache();
      // Signing out is not an expiry: drop the crumb so the next 401 stays
      // quiet.
      forgetSession();
      if (mountedRef.current) {
        setUser(null);
        setStatus('guest');
        setError(null);
      }
    }
  }, [clearUserScopedCache]);

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, error, login, verifyTwoFactor, register, logout, refresh }),
    [status, user, error, login, verifyTwoFactor, register, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
